import { Box, Card, CircularProgress, Grid, LinearProgress, Paper, Typography, useTheme } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import {
    CheckCircle as CheckCircleIcon,
    CalendarMonth as CalendarIcon,
    TrendingUp as TrendingUpIcon,
    Info as InfoIcon,
} from "@mui/icons-material";
import {
    usePredictionStatus,
    useCompletionForecast,
    useBudgetTrajectory,
    usePhaseDistribution,
    useTimelineData,
} from "../hooks";

export const PredictTheFuture = () => {
    const theme = useTheme();
    
    // Fetch all prediction data
    const { data: predictionStatus, isLoading: isStatusLoading } = usePredictionStatus();
    const { data: completionForecast, isLoading: isForecastLoading } = useCompletionForecast();
    const { data: budgetTrajectory, isLoading: isBudgetLoading } = useBudgetTrajectory();
    const { data: phaseDistribution = [], isLoading: isPhaseLoading } = usePhaseDistribution();
    const { data: timelineData = [], isLoading: isTimelineLoading } = useTimelineData();
    
    const isLoading = isStatusLoading || isForecastLoading || isBudgetLoading || isPhaseLoading || isTimelineLoading;
    
    if (isLoading) {
        return (
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ height: "100%", overflow: "auto", px: 2, py: 3 }}>
            <Grid container spacing={3}>
                {/* Predictions Enabled Card */}
                <Grid size={12}>
                    <Card
                        variant="outlined"
                        sx={{
                            p: 3,
                            borderLeft: `4px solid ${theme.palette.success.main}`,
                            bgcolor: "rgba(46, 125, 50, 0.05)",
                        }}
                    >
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: .5 }}>
                                <CheckCircleIcon color="success" sx={{ fontSize: 24 }} />
                                <Typography variant="body2">
                                    Predictions Enabled
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                {predictionStatus?.description}
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    mb: 0.5,
                                }}
                            >
                                <Typography variant="body2">
                                    Data Completeness
                                </Typography>
                                <Typography variant="body2" color="success.main">
                                    {predictionStatus?.dataCompleteness ?? 0}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={predictionStatus?.dataCompleteness ?? 0}
                                sx={{
                                    height: 8,
                                    borderRadius: 1,
                                    bgcolor: "rgba(46, 125, 50, 0.1)",
                                    "& .MuiLinearProgress-bar": {
                                        bgcolor: "success.main",
                                    },
                                }}
                            />
                        </Box>
                    </Card>
                </Grid>

                {/* Completion Range Forecast */}
                <Grid size={12}>
                    <Card variant="outlined" sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <CalendarIcon color="primary" sx={{ fontSize: 24 }} />
                                <Typography variant="body2">
                                    Completion Range Forecast
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                Based on existing Planned Close-out Dates
                            </Typography>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Paper
                                    sx={{
                                        p: 2,
                                        bgcolor: "rgba(25, 118, 210, 0.05)",
                                        border: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <Typography variant="caption" color="primary.main">
                                        Earliest Completion
                                    </Typography>
                                    <Typography variant="body1" color="primary.main">
                                        {completionForecast?.earliest}
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Paper
                                    sx={{
                                        p: 2,
                                        bgcolor: "rgba(156, 39, 176, 0.05)",
                                        border: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{ color: "#9c27b0" }}

                                    >
                                        Latest Completion
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: "#9c27b0" }}>
                                        {completionForecast?.latest}
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        <Box sx={{ bgcolor: "rgba(25, 118, 210, 0.05)", p: 1.5, borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                <strong>Forecast:</strong> {completionForecast?.description}
                            </Typography>
                        </Box>

                    </Card>
                </Grid>

                {/* Budget Trajectory */}
                <Grid size={12}>
                    <Card variant="outlined" sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <TrendingUpIcon sx={{ fontSize: 24, color: "#00897b" }} />
                                <Typography variant="body2">
                                    Budget Trajectory
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                Value in active delivery phases
                            </Typography>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Paper
                                    sx={{
                                        p: 2,
                                        bgcolor: "rgba(0, 137, 123, 0.05)",
                                        border: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{ color: "#00897b" }}

                                    >
                                        Active Projects
                                    </Typography>
                                    <Typography variant="h4" sx={{ color: "#00897b" }}>
                                        {budgetTrajectory?.activeProjects ?? 0}
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Paper
                                    sx={{
                                        p: 2,
                                        bgcolor: "rgba(0, 137, 123, 0.05)",
                                        border: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{ color: "#00897b" }}

                                    >
                                        Active Budget
                                    </Typography>
                                    <Typography variant="h4" sx={{ color: "#00897b" }}>
                                        £{budgetTrajectory?.activeBudget ?? 0}M
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        <Box sx={{ bgcolor: "rgba(0, 137, 123, 0.05)", p: 1.5, borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                {budgetTrajectory?.description}
                            </Typography>
                        </Box>

                    </Card>
                </Grid>

                {/* Integrity Outlook */}
                <Grid size={12}>
                    <Card variant="outlined" sx={{ p: 3 }}>
                        <Typography variant="body2">
                            Integrity Outlook
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 3 }}>
                            Planning credibility and phase distribution forecast
                        </Typography>

                        <Box sx={{ width: "100%", height: 300 }}>
                            <BarChart
                                dataset={phaseDistribution}
                                xAxis={[
                                    {
                                        scaleType: "band",
                                        dataKey: "phase",
                                        tickLabelStyle: {
                                            angle: -45,
                                            textAnchor: "end",
                                            fontSize: 11,
                                        },
                                    },
                                ]}
                                series={[
                                    {
                                        dataKey: "projects",
                                        label: "Projects",
                                        color: theme.palette.primary.main,
                                    },
                                ]}
                                height={300}
                                margin={{ bottom: 80, left: 50, right: 20, top: 20 }}
                            />
                        </Box>

                        <Box sx={{ bgcolor: "rgba(0, 137, 123, 0.05)", p: 1.5, borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                <strong>Integrity Outlook:</strong> Current phase distribution shows concentration in
                                Construction (SG4) and Detailed Design. Monitor critical path integrity for projects
                                approaching Construction and Commissioning phases.
                            </Typography>
                        </Box>

                    </Card>
                </Grid>

                {/* Projected Timeline */}
                <Grid size={12}>
                    <Card variant="outlined" sx={{ p: 3 }}>
                        <Typography variant="body2">
                            Projected Timeline (Illustrative)
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 3 }}>
                            Expected project flow if current plans hold
                        </Typography>

                        <Box sx={{ width: "100%", height: 300 }}>
                            <LineChart
                                dataset={timelineData}
                                xAxis={[
                                    {
                                        scaleType: "point",
                                        dataKey: "date",
                                    },
                                ]}
                                series={[
                                    {
                                        dataKey: "activeProjects",
                                        label: "Active Projects",
                                        color: "#ff9800",
                                        curve: "natural",
                                    },
                                    {
                                        dataKey: "completedProjects",
                                        label: "Completed Projects",
                                        color: "#00897b",
                                        curve: "natural",
                                    },
                                ]}
                                height={300}
                                margin={{ bottom: 50, left: 50, right: 20, top: 20 }}
                            />
                        </Box>
                        <Box sx={{ bgcolor: "rgba(0, 137, 123, 0.05)", p: 1.5, borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                This chart illustrates potential project flow based on planned dates. Actual outcomes
                                depend on delivery execution and risk mitigation.
                            </Typography>
                        </Box>
                    </Card>
                </Grid>

                {/* Forecast Methodology */}
                <Grid size={12}>
                    <Card
                        variant="outlined"
                        sx={{
                            p: 3,
                            borderLeft: `4px solid ${theme.palette.info.main}`,
                            bgcolor: "rgba(25, 118, 210, 0.05)",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                            <InfoIcon color="info" sx={{ fontSize: 24 }} />
                            <Typography variant="body2">
                                Forecast Methodology
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <Box
                                sx={{
                                    p: 1.5,
                                    bgcolor: "background.paper",
                                    borderRadius: 1,
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Typography variant="body2">
                                    <strong>Data Window:</strong> All projects in selected scope with valid date fields
                                    (Planned End Date, Commissioning Date, Close-out Date).
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    p: 1.5,
                                    bgcolor: "background.paper",
                                    borderRadius: 1,
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Typography variant="body2">
                                    <strong>Completeness:</strong> {predictionStatus?.dataCompleteness ?? 0}% of
                                    projects have complete date data.
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    p: 1.5,
                                    bgcolor: "background.paper",
                                    borderRadius: 1,
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Typography variant="body2">
                                    <strong>Assumptions:</strong> Forecasts assume no further changes to planned dates.
                                    Real delivery will vary based on risk events, resource availability, and management
                                    interventions.
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    p: 1.5,
                                    bgcolor: "background.paper",
                                    borderRadius: 1,
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Typography variant="body2">
                                    <strong>Confidence:</strong> Bounded ranges only. Vision Studio provides decision
                                    support, not automated predictions. Human judgment remains essential.
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    p: 1.5,
                                    bgcolor: "rgba(25, 118, 210, 0.08)",
                                    borderRadius: 1,
                                }}
                            >
                                <Typography variant="body2">
                                    <strong>Important:</strong> These predictions will improve as data quality increases
                                    and when dependency data becomes available in future releases.
                                </Typography>
                            </Box>
                        </Box>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};
